// api.js

import axiosInstance from "../axiosInstance";

export const forecastDataService = {
  // Populates `input.product`/`input.warehouse` the same way `list()` does,
  // so a single fetched run (e.g. right after it finishes, or via the
  // forecast agent's getForecastResults tool) shows names instead of raw ids.
  // Re-run only the SKU insight for an Updated Forecast run (e.g. after it
  // failed). Resolves to { status: "pending", insight_requested_at }; the
  // insight then arrives like the first time (WebSocket push / polling).
  regenerateInsight: async (id) => {
    const response = await axiosInstance.post("/forecast-insight-request", {
      forecast_data_id: id,
    });
    return response.data;
  },

  get: async (id, { signal } = {}) => {
    try {
      const params = [
        `$populate[0][path]=input.product`,
        `$populate[1][path]=input.warehouse`,
      ];
      let response = await axiosInstance.get(
        `/forecast-data/${id}?${params.join("&")}`,
        { signal },
      );
      return response;
    } catch (e) {
      throw e;
    }
  },

  // Recent forecast runs for a client, newest first. Populates the product and
  // warehouse referenced by each run's `input` so the params panel can show
  // their names without extra lookups. `product` scopes to one product,
  // `sku` does a substring match, `skuExact` an exact match. `forecastBy`
  // ("product" | "sku") scopes to runs actually generated at that level —
  // e.g. a lone SKU re-run under a product won't be returned when browsing
  // that product's history with `forecastBy: "product"`.
  list: async (
    clientId,
    { limit = 20, skip = 0, sku, skuExact, product, forecastBy, engine, signal } = {},
  ) => {
    try {
      const params = [
        `$sort[createdAt]=-1`,
        `$limit=${limit}`,
        `$skip=${skip}`,
        `$populate[0][path]=input.product`,
        `$populate[1][path]=input.warehouse`,
      ];
      if (clientId) params.push(`client=${clientId}`);
      if (product) params.push(`product=${encodeURIComponent(product)}`);
      if (skuExact) params.push(`sku=${encodeURIComponent(skuExact)}`);
      if (forecastBy) params.push(`forecastBy=${encodeURIComponent(forecastBy)}`);
      // "demand_forecast" → only the admin Updated Forecast page's runs;
      // omitted → every other run (the server excludes those by default).
      if (engine) params.push(`engine=${encodeURIComponent(engine)}`);
      if (sku) {
        params.push(`sku[$regex]=${encodeURIComponent(sku)}`);
        params.push(`sku[$options]=i`);
      }
      const response = await axiosInstance.get(
        `/forecast-data?${params.join("&")}`,
        { signal },
      );
      return response.data;
    } catch (e) {
      throw e;
    }
  },

  // History browser options: one row per distinct product / SKU that appears in
  // this client's stored forecast runs, newest activity first. Paginated so the
  // dropdown can "load more". `by` is "product" | "sku".
  // → { total, limit, skip, by, data: [{ value, label, ... }] }
  groups: async (
    clientId,
    { by = "product", limit = 20, skip = 0, search, engine, signal } = {},
  ) => {
    try {
      const params = [`by=${by}`, `limit=${limit}`, `skip=${skip}`];
      if (clientId) params.push(`client=${clientId}`);
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (engine) params.push(`engine=${encodeURIComponent(engine)}`);
      const response = await axiosInstance.get(
        `/forecast-data-groups?${params.join("&")}`,
        { signal },
      );
      return response.data;
    } catch (e) {
      throw e;
    }
  },
};
